import React, { FunctionComponent } from 'react';
import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  CircularProgress
} from '@mui/material';

const BUTTON_VARIANTS = ['contained', 'outlined', 'text'] as const;
type ButtonVariant = typeof BUTTON_VARIANTS[number];

const BUTTON_COLORS = ['primary', 'secondary', 'error', 'info', 'success', 'warning'] as const;
type ButtonColor = typeof BUTTON_COLORS[number];

const BUTTON_SIZES = ['small', 'medium', 'large'] as const;
type ButtonSize = typeof BUTTON_SIZES[number];

interface ButtonProps extends Omit<MuiButtonProps, 'variant' | 'color' | 'size'> {
  variant?: ButtonVariant;
  color?: ButtonColor;
  size?: ButtonSize;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
  fullWidth?: boolean;
  loading?: boolean;
  className?: string;
  'aria-label'?: string;
  debounceTime?: number;
}

export const Button: FunctionComponent<ButtonProps> = React.memo(({
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  disabled = false,
  onClick,
  children,
  fullWidth = false,
  loading = false,
  className = '',
  'aria-label': ariaLabel,
  debounceTime = 300,
  ...props
}) => {
  const lastClickRef = React.useRef<number>(0);

  const handleClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const now = Date.now();
    if (onClick && now - lastClickRef.current >= debounceTime) {
      lastClickRef.current = now;
      onClick(event);
    }
  }, [onClick, debounceTime]);

  const ErrorFallback: React.FC = () => (
    <MuiButton
      variant={variant}
      color="error"
      size={size}
      disabled
      fullWidth={fullWidth}
      className={className}
    >
      Error
    </MuiButton>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <React.Fragment>
          <CircularProgress
            size={size === 'small' ? 16 : size === 'large' ? 24 : 20}
            color="inherit"
            style={{ marginRight: '8px' }}
          />
          {children}
        </React.Fragment>
      );
    }
    return children;
  };

  try {
    return (
      <MuiButton
        variant={variant}
        color={color}
        size={size}
        disabled={disabled || loading}
        onClick={handleClick}
        fullWidth={fullWidth}
        className={className}
        aria-label={ariaLabel}
        {...props}
      >
        {renderContent()}
      </MuiButton>
    );
  } catch (error) {
    console.error('Button rendering error:', error);
    return <ErrorFallback />;
  }
});

Button.displayName = 'Button';

export type { ButtonProps, ButtonVariant, ButtonColor, ButtonSize };